import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { startTransition, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';

import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import {
  DEFAULT_LIST_PAGE_SIZE, LIST_FILTER_ALL, LOAD_MORE, REFETCH,
} from 'src/util/constants';
import logger from 'src/util/logger';
import { dateToTimeInTimezone, getTimezone } from 'src/util/time';

import AdminMeterTradeRulesTable from './AdminMeterTradeRulesTable';
import AdminTradeRulesControls from '../../AdminTradeRuleControls';

const AdminMeterTradeRulesFragment = graphql`
  fragment AdminMeterTradeRules_meter on Meter
  @throwOnFieldError
  @refetchable(queryName: "AdminMeterTradeRulesRefetchQuery")
  @argumentDefinitions(
    count: { type: "Int" }
    cursor: { type: "String" }
    state: { type: "TradeRuleState" }
    type: { type: "TradeType" }
    start: { type: "Timestamp" }
    finish: { type: "Timestamp" }
  )
  {
    uuid
    identifier
    title
    tradePointId
    property {
      uuid
      timezone
    }
    rules(
      first: $count
      after: $cursor
      state: $state
      type: $type
      start: $start
      finish: $finish
    ) @connection(key: "AdminMeterTradeRules_rules") {
      edges {
        cursor
        node {
          uuid
        }
      }
      ...AdminMeterTradeRulesTable_rules
    }
  }
`;

/**
 * Builds the URL query string that mirrors the current trade rule filters.
 * @param {object} filter - Current filter.
 * @param {string|null} filter.state - Trade rule state filter.
 * @param {string|null} filter.type - Trade type filter.
 * @param {DateTime|null} date - Selected date.
 * @param {string} timezone - Property timezone.
 * @returns {string} The `search` part of the URL, with a leading `?`, or an empty string.
 */
function buildFilterSearch({ state, type }, date, timezone) {
  const queryParameters = new URLSearchParams();
  if (state) {
    queryParameters.append('state', state);
  }
  if (type) {
    queryParameters.append('type', type);
  }
  if (date) {
    queryParameters.append('date', date.toISODate({ zone: timezone }));
  }
  const search = queryParameters.toString();
  return search ? `?${search}` : '';
}

/**
 * Admin view of a meter's trade rules, filterable by date, state and type, with
 * load-more pagination. Field errors throw to the route's PlaceholderErrorBoundary;
 * load-more failures show an inline retry modal.
 * @param {object} props - Component props.
 * @param {object|null} props.meter - Relay fragment reference for the meter.
 * @param {object} props.match - Found route match, used for the current pathname.
 * @param {object} props.router - Found router, used to sync filters into the URL.
 * @param {object} props.variables - Route query variables holding the initial filters.
 * @param {Function} props.filtersAppliedFunc - Called with the refetch variables after
 *   a filter or date refetch succeeds, so the route can retry with them.
 * @returns {React.ReactComponentElement} AdminMeterTradeRules component.
 */
function AdminMeterTradeRules({
  meter: meterRef = null, filtersAppliedFunc, match, router, variables,
}) {
  const intl = useIntl();
  const {
    data: meter, loadNext, hasNext, isLoadingNext, refetch,
  } = usePaginationFragment(AdminMeterTradeRulesFragment, meterRef);
  const timezone = getTimezone(meter?.property?.timezone);

  const [date, setDate] = useState(() => (
    variables?.start ? DateTime.fromSeconds(variables.start, { zone: timezone }) : null
  ));
  const [filter, setFilter] = useState({
    state: variables?.state || null,
    type: variables?.type || null,
  });
  const [updating, setUpdating] = useState(null);
  const [paginationError, setPaginationError] = useState(null);

  if (!meter) {
    // A completed query without a meter would otherwise never leave the spinner.
    throw new Error('AdminMeterTradeRules: the route query returned no meter');
  }

  /**
   * Refetches trade rules for the given filters and, on success, mirrors them
   * into the URL.
   * @param {object} nextFilter - Filter to apply.
   * @param {DateTime|null} nextDate - Date to apply.
   */
  const doRefetch = (nextFilter, nextDate) => {
    const refetchVariables = {
      count: DEFAULT_LIST_PAGE_SIZE,
      state: nextFilter.state,
      type: nextFilter.type,
      start: null,
      finish: null,
    };
    if (nextDate) {
      const timeInTimezone = dateToTimeInTimezone(nextDate, timezone);
      refetchVariables.start = timeInTimezone.startOf('day').toSeconds();
      refetchVariables.finish = timeInTimezone.startOf('day').plus({ days: 1 }).toSeconds();
    }

    // The table shows a spinner in place of the rules while `updating` is REFETCH.
    setUpdating(REFETCH);
    // A transition keeps the refetch from suspending the whole route to its
    // Suspense fallback; the heading, filters and table frame stay mounted.
    startTransition(() => {
      refetch(refetchVariables, {
        fetchPolicy: 'network-only',
        // A failed refetch throws to the route's PlaceholderErrorBoundary, whose
        // retry reloads the last applied filters, which are also the ones in the URL.
        onComplete: (error) => {
          if (!error) {
            filtersAppliedFunc(refetchVariables);
            // The refetch already used the property timezone. Rematching would
            // rerun the route's prepareVariables, which falls back to DEFAULT_TIMEZONE
            // without a tz param, and replace these results with a different day.
            router.replace({
              pathname: match?.location?.pathname,
              search: buildFilterSearch(nextFilter, nextDate, timezone),
              hash: '',
              doNotRerunMatch: true,
            });
          }
          setUpdating(null);
        },
      });
    });
  };

  /**
   * Updates the selected date and refetches.
   * @param {DateTime} nextDate - The selected date.
   */
  const dateUpdateFunc = (nextDate) => {
    if (updating) {
      return;
    }

    setDate(nextDate);
    doRefetch(filter, nextDate);
  };

  /**
   * Updates the state or type filter and refetches. Selecting the active value
   * clears it; `LIST_FILTER_ALL` clears every filter and the date.
   * @param {string} key - The filter key to update (`state` or `type`).
   * @param {string} value - The new filter value.
   */
  const filterFunc = (key, value) => {
    if (updating) {
      return;
    }

    const nextFilter = { ...filter };
    let nextDate = date;

    if (value === LIST_FILTER_ALL) {
      nextFilter.state = null;
      nextFilter.type = null;
      nextDate = null;
    } else if (filter[key] === value) {
      nextFilter[key] = null;
    } else {
      nextFilter[key] = value;
    }

    setFilter(nextFilter);
    setDate(nextDate);
    doRefetch(nextFilter, nextDate);
  };

  /**
   * Loads the next page of trade rules. A failure keeps the loaded rules and
   * shows the inline pagination error modal.
   */
  const loadMore = () => {
    if (!hasNext || isLoadingNext || updating) {
      return;
    }

    setPaginationError(null);
    setUpdating(LOAD_MORE);
    loadNext(DEFAULT_LIST_PAGE_SIZE, {
      onComplete: (error) => {
        if (error) {
          logger.error({ error });
          setPaginationError(error);
        }
        setUpdating(null);
      },
    });
  };

  const {
    uuid, identifier, rules, title,
  } = meter || {};

  // Page title and headings
  const pageTitle = intl.formatMessage(
    {
      id: 'admin.admin_meter.admin_meter_trade_rules.page.title',
      defaultMessage: 'Enosi Admin - Meter - {identifier} - Trade Rules',
    },
    { identifier },
  );
  const headingContext = intl.formatMessage({
    id: 'admin.admin_meter.admin_meter_trade_rules.heading.context',
    defaultMessage: 'Trade Rules',
  });

  // Breadcrumb labels
  const breadcrumbAdmin = intl.formatMessage({
    id: 'admin.admin_meter.admin_meter_trade_rules.breadcrumb.admin',
    defaultMessage: 'Admin',
  });
  const breadcrumbMeters = intl.formatMessage({
    id: 'admin.admin_meter.admin_meter_trade_rules.breadcrumb.meters',
    defaultMessage: 'Meters',
  });
  const breadcrumbMeter = intl.formatMessage(
    {
      id: 'admin.admin_meter.admin_meter_trade_rules.breadcrumb.meter',
      defaultMessage: '{title} ({identifier})',
    },
    { identifier, title },
  );
  const breadcrumbTradeRules = intl.formatMessage({
    id: 'admin.admin_meter.admin_meter_trade_rules.breadcrumb.trade_rules',
    defaultMessage: 'Trade Rules',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbMeters, path: '/admin/meters' },
          { name: breadcrumbMeter, path: `/admin/meters/${uuid}` },
          { name: breadcrumbTradeRules },
        ]}
      />

      <Heading
        title={identifier}
        context={headingContext}
      />

      <AdminTradeRulesControls
        date={date}
        dateUpdateFunc={dateUpdateFunc}
        filter={filter}
        filterFunc={filterFunc}
      />

      <AdminMeterTradeRulesTable
        meter={meter}
        rules={rules}
        moreFunc={loadMore}
        hasMore={hasNext}
        updating={updating}
        paginationError={paginationError}
        paginationErrorCloseFunc={() => setPaginationError(null)}
      />
    </>
  );
}

AdminMeterTradeRules.propTypes = {
  meter: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  filtersAppliedFunc: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  router: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
  variables: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default AdminMeterTradeRules;
